import { motion } from "framer-motion";
import { useState } from "react";
import Navigation from "@/components/Navigation";
import FooterColumn from "@/components/ui/footer-column";
import BrandAnalyzerDialog from "@/components/BrandAnalyzerDialog";
import AnimatedShaderBackground from "@/components/ui/animated-shader-background";
import BrandReportClientsSection from "@/components/BrandReportClientsSection";
import { BouncyCardsFeatures } from "@/components/ui/bounce-card-features";
import { BarChart3, Building2, Users, MessageSquare, Target, ArrowRight, TrendingUp } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SEO from "@/components/SEO";
import { HeroHighlight, Highlight } from "@/components/ui/hero-highlight";
import { InfiniteSlider } from "@/components/ui/infinite-slider";
import { ClientLogosSlider } from "@/components/ui/ClientLogosSlider";
import brandPerceptionImage from "@/assets/brand-perception-mirror.png";
const GetFreeBrandReportPage = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  return <div className="min-h-screen bg-white relative">
      <SEO data={{
      title: "Free Brand Analysis & Content Strategy | gimmefy",
      description: "Get a free, AI-powered brand analysis report covering business profile, tone of voice, audience insights, competitive landscape, and more. Includes 1000 free credits and demo account.",
      keywords: "free brand analysis, brand analyzer, AI brand report, content strategy, brand audit, competitive analysis, SWOT analysis, brand assessment",
      imageUrl: "/lovable-uploads/hero-ai-marketing.jpg"
    }} canonicalUrl="https://gimmefy.ai/get-free-brand-report" />
      
      {/* Fixed Background - Outside of scroll container */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-5">
        <AnimatedShaderBackground />
      </div>
      
      {/* Scrollable Content Container */}
      <div className="relative z-20 overflow-x-hidden pb-4">
        <Navigation />
        
        {/* Hero Section */}
        <HeroHighlight containerClassName="relative z-20 mb-16 md:mb-12 lg:mb-16" className="pt-24 md:pt-20 lg:pt-16 pb-16 md:pb-16 lg:pb-20">
          <div className="container mx-auto px-4 max-w-7xl">
            {/* Full Width Title */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="mb-8 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-neutral-700 dark:text-white leading-[1.35] md:leading-[1.25] lg:leading-[1.2]">
                Discover The Way The World Sees <Highlight className="text-black dark:text-white">Your Brand</Highlight>
              </h1>
            </motion.div>

            {/* Two Column Layout - Image Left, Text Right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Column - Image */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img src={brandPerceptionImage} alt="Brand perception - how your brand sees itself versus how the world sees it, illustrated with elegant mirror reflection" className="w-full h-auto object-cover" />
                </div>
              </motion.div>

              {/* Right Column - Text Content */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="space-y-6">
                <h2 className="text-2xl md:text-3xl font-semibold text-foreground/90">
                  Turn Your Brand's Digital Footprint Into Insight
                </h2>
                
                <p className="text-lg md:text-xl text-muted-foreground">
                  Most brand teams build their story from the inside out, shaping how they want to be seen.
                  But how your brand actually shows up online can be very different.
                  Our 360° AI Brand Report reveals that truth; comparing your internal identity to your external digital footprint across customers, competitors, and search visibility.
                  Built on gimmefy's USD 3,000 Brand Vault system.
                </p>
                
                {/* CTA Button */}
            <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.4
              }} className="pt-4 mb-8 md:mb-8 lg:mb-6">
                  <button onClick={() => setIsDialogOpen(true)} className="px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                    Get Your Free Report
                  </button>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </HeroHighlight>

        {/* Client Logos Slider */}
        <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="relative z-20 -mt-8 md:-mt-12">
          <ClientLogosSlider />
        </motion.div>

        {/* Single Column Section: Horizontal Row + 2-Column Layout */}
        <section className="relative z-20 bg-gray-50/50 border-t border-gray-200 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-foreground mb-4">What Your 360° AI Brand Report Includes</h2>
              <p className="text-xl text-muted-foreground text-center max-w-2xl mx-auto mb-12">A complete snapshot of your brand's digital presence, analyzed by AI</p>
            </motion.div>

            {/* Responsive Grid - First 5 Benefits */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-5 gap-4 [&>*:nth-child(n+1):nth-child(-n+3)]:md:max-lg:col-span-2 [&>*:nth-child(n+4)]:md:max-lg:col-span-3">
                {[{
                  icon: Building2,
                  title: "Business Profile",
                  description: "Your brand at a glance; USPs, mission, and audience, clearly defined.",
                  gradient: "from-blue-500/20 to-cyan-500/20",
                  iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500",
                  iconColor: "text-white"
                }, {
                  icon: MessageSquare,
                  title: "Tone of Voice",
                  description: "Your brand's personality and how it sounds online.",
                  gradient: "from-purple-500/20 to-pink-500/20",
                  iconBg: "bg-gradient-to-br from-purple-500 to-pink-500",
                  iconColor: "text-white"
                }, {
                  icon: Users,
                  title: "Audience Profile",
                  description: "Who they are, what influences them and their purchasing habits.",
                  gradient: "from-orange-500/20 to-red-500/20",
                  iconBg: "bg-gradient-to-br from-orange-500 to-red-500",
                  iconColor: "text-white"
                }, {
                  icon: Target,
                  title: "Customer Analysis",
                  description: "How people perceive your brand, and also any patterns or trends.",
                  gradient: "from-green-500/20 to-emerald-500/20",
                  iconBg: "bg-gradient-to-br from-green-500 to-emerald-500",
                  iconColor: "text-white"
                }, {
                  icon: BarChart3,
                  title: "Competitive Landscape",
                  description: "A deep understanding of rivals; strengths, gaps, and industry shifts.",
                  gradient: "from-indigo-500/20 to-violet-500/20",
                  iconBg: "bg-gradient-to-br from-indigo-500 to-violet-500",
                  iconColor: "text-white"
                }].map((item, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className="relative overflow-hidden rounded-xl p-6 bg-white border border-gray-200 shadow-sm hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onMouseEnter={e => {
                  const gradient = item.gradient.replace('/20', '/5');
                  e.currentTarget.style.background = `linear-gradient(to bottom right, ${gradient})`;
                }} onMouseLeave={e => {
                  e.currentTarget.style.background = 'white';
                }}>
                    <div className="flex flex-col gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-14 h-14 rounded-xl ${item.iconBg} flex items-center justify-center shadow-lg`}>
                          <item.icon className={`w-7 h-7 ${item.iconColor}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-base mb-2 text-foreground">{item.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="text-center mt-8 mb-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button onClick={() => setIsDialogOpen(true)} className="px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                Claim My Brand Report
              </button>
              <button onClick={() => {
                const sampleSection = document.getElementById('sample-brand-report-section');
                sampleSection?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }} className="px-8 py-4 bg-white border-2 border-primary text-primary font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2">
                View Sample Report
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3 Simple Steps Section */}
      <section className="relative z-20 bg-white border-t border-gray-200 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-foreground mb-4">
                Your Brand&apos;s Mugshot, in 3 Simple Steps
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">All it takes from you is just a few minutes.</p>
            </motion.div>

            {/* Steps Flow */}
            <div className="relative">
              {/* Mobile Infinite Slider */}
              <div className="md:hidden">
                <InfiniteSlider duration={15} gap={24} className="py-8">
                  {[{
                    number: "1",
                    title: "Submit Your Brand",
                    description: "Share your website and what you'd like us to uncover.",
                    gradient: "from-blue-500 to-cyan-500"
                  }, {
                    number: "2",
                    title: "gimmefy's Brand Vault",
                    description: "Our AI runs an online audit based on the url you provide.",
                    gradient: "from-purple-500 to-pink-500"
                  }, {
                    number: "3",
                    title: "Receive Your Report",
                    description: "Get a full breakdown + bonus topic research via email.",
                    gradient: "from-orange-500 to-red-500"
                  }].map((step, index) => <div key={index} className="flex flex-col items-center text-center w-[280px] flex-shrink-0">
                      {/* Number Badge */}
                      <div className={`relative z-10 w-32 h-32 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg mb-6`}>
                        <span className="text-5xl font-bold text-white">{step.number}</span>
                      </div>

                      {/* Content */}
                      <div className="space-y-3">
                        <h3 className="text-xl font-bold text-foreground">
                          {step.title}
                        </h3>
                        <p className="text-base text-muted-foreground leading-relaxed max-w-xs mx-auto">
                          {step.description}
                        </p>
                      </div>
                    </div>)}
                </InfiniteSlider>
              </div>

              {/* Tablet/Desktop Grid */}
              <div className="hidden md:block">
                <div className="grid grid-cols-3 gap-6">
                  {[{
                    number: "1",
                    title: "Submit Your Brand",
                    description: "Share your website and what you'd like us to uncover.",
                    gradient: "from-blue-500 to-cyan-500"
                  }, {
                    number: "2",
                    title: "gimmefy's Brand Vault",
                    description: "Our AI runs an online audit based on the url you provide.",
                    gradient: "from-purple-500 to-pink-500"
                  }, {
                    number: "3",
                    title: "Receive Your Report",
                    description: "Get a full breakdown + bonus topic research via email.",
                    gradient: "from-orange-500 to-red-500"
                  }].map((step, index) => <motion.div key={index} initial={{
                    opacity: 0,
                    y: 30
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.6,
                    delay: index * 0.2
                  }} className="relative flex flex-col items-center text-center">
                      {/* Number Badge */}
                      <div className={`relative z-10 w-32 h-32 rounded-full bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg mb-6`}>
                        <span className="text-5xl font-bold text-white">{step.number}</span>
                      </div>

                      {/* Arrow connector - only show between steps on desktop */}
                      {index < 2 && <div className="hidden lg:block absolute top-16 -right-3 transform translate-x-1/2 z-20">
                          <ArrowRight className="w-6 h-6 text-primary/60" />
                        </div>}

                      {/* Content */}
                      <div className="space-y-3">
                        <h3 className="text-xl font-bold text-foreground">
                          {step.title}
                        </h3>
                        <p className="text-base text-muted-foreground leading-relaxed max-w-xs mx-auto">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="text-center mt-12">
              <button onClick={() => setIsDialogOpen(true)} className="px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                Start My Brand Analysis
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sample Brand Report Section */}
      <section id="sample-brand-report-section" className="relative z-20 bg-white border-t border-gray-200 py-16 md:py-24 overflow-hidden">
        {/* Floating decorative shapes */}
        <div className="absolute inset-0 pointer-events-none z-0">
          {/* Left side shapes */}
          <div className="absolute top-20 -left-32 w-64 h-64 bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-40 -left-20 w-80 h-80 bg-gradient-to-br from-green-200/40 to-emerald-200/40 rounded-full blur-3xl animate-pulse" style={{
            animationDelay: '1.5s'
          }} />
          
          {/* Right side shapes */}
          <div className="absolute top-40 -right-32 w-96 h-96 bg-gradient-to-br from-purple-200/40 to-pink-200/40 rounded-full blur-3xl animate-pulse" style={{
            animationDelay: '1s'
          }} />
          <div className="absolute bottom-20 -right-20 w-72 h-72 bg-gradient-to-br from-orange-200/40 to-yellow-200/40 rounded-full blur-3xl animate-pulse" style={{
            animationDelay: '2s'
          }} />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-foreground mb-4">
            See What You'll Get: Sample Brand Report
          </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Here's a real example of our AI-powered analysis for NESCAFÉ
              </p>
            </motion.div>

            {/* Tabs */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
              <Tabs defaultValue="business-profile" className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-5 h-auto gap-2 bg-gray-100 p-2">
                  <TabsTrigger value="business-profile" className="flex items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white">
                    <Building2 className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden sm:inline">Business Profile</span>
                    <span className="sm:hidden">Business</span>
                  </TabsTrigger>
                  <TabsTrigger value="tone" className="flex items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-500 data-[state=active]:text-white">
                    <MessageSquare className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden sm:inline">Tone of Voice</span>
                    <span className="sm:hidden">Tone</span>
                  </TabsTrigger>
                  <TabsTrigger value="audience" className="flex items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-500 data-[state=active]:text-white">
                    <Users className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden sm:inline">Audience</span>
                    <span className="sm:hidden">Audience</span>
                  </TabsTrigger>
                  <TabsTrigger value="insights" className="flex items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-500 data-[state=active]:text-white">
                    <TrendingUp className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden sm:inline">Customer Insights</span>
                    <span className="sm:hidden">Insights</span>
                  </TabsTrigger>
                  <TabsTrigger value="competitive" className="flex items-center gap-2 py-3 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-blue-500 data-[state=active]:text-white">
                    <BarChart3 className="w-5 h-5 flex-shrink-0" />
                    <span className="hidden sm:inline">Competitive Landscape</span>
                    <span className="sm:hidden">Market</span>
                  </TabsTrigger>
                </TabsList>

                {/* Business Profile Tab */}
                <TabsContent value="business-profile" className="mt-6">
                  <div className="bg-gradient-to-br from-blue-50/30 via-white to-cyan-50/30 rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 max-h-[600px] overflow-y-auto">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Brand Name:</h3>
                        <p className="text-muted-foreground">NESCAFÉ</p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Description:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          NESCAFÉ, part of Société des Produits Nestlé S.A., is a global leader in the coffee industry dedicated to crafting exceptional instant and specialty coffee products. Embracing innovation, NESCAFÉ offers a wide variety of blends, roasts, and formats—including instant coffee, espresso concentrates, and single-serve packets—meeting the evolving needs of consumers around the world. The company sources beans responsibly from top-growing regions, emphasizing quality, sustainability, and community support. Its goal is to make premium coffee experiences accessible at home and on the go while committing to environmental responsibility through sustainable packaging and sourcing practices.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Mission:</h3>
                        <p className="text-muted-foreground">
                          To craft delicious coffee that enriches everyday moments and champions sustainability.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Products & Services:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          NESCAFÉ offers an extensive range of coffee solutions tailored to all palates and preferences—from bold dark roasts like NESCAFÉ Clásico™ to smooth, barista-style options like NESCAFÉ Gold Espresso and refreshing blends such as NESCAFÉ Ice Roast. Every product delivers quick preparation and consistent taste, ensuring convenience without sacrificing quality or flavor. Using responsibly sourced beans, NESCAFÉ ensures freshness and ethical practices across its offerings, including espresso concentrates for café-worthy iced beverages and easy-to-make recipe ideas, enhancing coffee rituals wherever customers are.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Unique Selling Points (USPs):</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Diverse product lineup across instant, espresso, and iced formats</li>
                          <li>Rapid preparation without machines; consistent flavor and instant crema</li>
                          <li>Sustainably sourced beans and recyclable packaging</li>
                          <li>Practical packaging and recipes designed for convenience and enjoyment</li>
                          <li>Commitment to full recyclability by 2025</li>
                          <li>A heritage brand known for global consistency and quality</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Target Audience:</h3>
                        <p className="text-muted-foreground">
                          Coffee enthusiasts seeking convenience, quality, and ethical choices.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-blue-700">Primary Markets:</h3>
                        <p className="text-muted-foreground">
                          Worldwide, with a strong presence across North America, Latin America, Europe, and Asia.
                        </p>
                      </div>
                      
                      {/* Compliance & Governance Section */}
                      <div className="pt-6 border-t border-gray-200">
                        <h3 className="font-bold text-xl mb-4 text-blue-700">Compliance & Governance</h3>
                        <div className="space-y-6">
                          <div>
                            <h4 className="font-bold text-lg mb-2 text-blue-700">Internal Governance Framework:</h4>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                              As part of Nestlé S.A., Nescafé follows the company's global Corporate Business Principles and Code of Business Conduct, aligned with international best practices such as the OECD Guidelines for Multinational Enterprises and the UN Global Compact.
                            </p>
                            <p className="text-muted-foreground mb-2">The framework emphasizes:</p>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                              <li><strong>Responsible Sourcing & Sustainability:</strong> Guided by the Nestlé Responsible Sourcing Standard and Nescafé Plan 2030, focusing on regenerative agriculture and farmer welfare</li>
                              <li><strong>Product Quality & Safety:</strong> Managed through Nestlé's Quality Management System (NQMS) and ISO-certified manufacturing processes</li>
                              <li><strong>Data Privacy:</strong> Compliant with GDPR and regional regulations under Nestlé's global privacy policy</li>
                              <li><strong>Ethical Marketing & Communication:</strong> Transparent, truthful, and culturally sensitive in all markets</li>
                              <li><strong>Internal Oversight:</strong> Independent audits and whistleblowing mechanisms ensure accountability and compliance</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-bold text-lg mb-2 text-blue-700">Regulatory & Industry Compliance Frameworks:</h4>
                            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                              <li><strong>ISO Standards:</strong> ISO 9001 (Quality Management), ISO 14001 (Environmental Management), ISO 22000 (Food Safety), ISO 45001 (Occupational Health & Safety)</li>
                              <li><strong>HACCP:</strong> Hazard Analysis and Critical Control Points for food safety</li>
                              <li><strong>GDPR:</strong> Global data protection and privacy compliance</li>
                              <li><strong>OECD & UN Global Compact:</strong> Ethical business, human rights, and sustainability standards</li>
                              <li>Nestlé Responsible Sourcing Standard</li>
                              <li><strong>FSSC 22000:</strong> Certified food safety management system</li>
                              <li><strong>Nescafé Plan 2030:</strong> Regenerative agriculture and sustainability reporting under GRI standards</li>
                              <li><strong>Local Food Authorities:</strong> Compliance with FDA (U.S.), EFSA (EU), and FSSAI (India)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Tone of Voice Tab */}
                <TabsContent value="tone" className="mt-6">
                  <div className="bg-gradient-to-br from-orange-50/30 via-white to-red-50/30 rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 max-h-[600px] overflow-y-auto">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-orange-700">Personality (3 Words):</h3>
                        <p className="text-muted-foreground">Welcoming, Innovative, Authentic</p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-orange-700">Writing Style:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          Friendly, inclusive, and uplifting with a touch of expertise. NESCAFÉ maintains an approachable yet knowledgeable tone—modern, confident, and reassuring—inviting everyone to create their own coffee moments.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-orange-700">Audience Experience Goal:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          After engaging with NESCAFÉ, the audience should feel energized, inspired, and connected—confident that great coffee can elevate their day, anywhere, anytime, while supporting sustainability.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-orange-700">Example Copy (Brand Voice):</h3>
                        <p className="text-muted-foreground leading-relaxed italic">
                          "Make Your Day Tastier. Enjoy Iced Espresso, just the way you like it—pour over ice and mix to hack your own café favorites at home. No machine, no compromise: our instant espresso blend creates café-style crema in seconds. Wake up, pour a cup, and experience perfection—because great coffee begins at its origins and is crafted for your day."
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-orange-700">Ideation Approach:</h3>
                        <p className="text-muted-foreground">
                          User-centric, inspired by real-life rituals, daily habits, and sustainable choices.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-orange-700">Language:</h3>
                        <p className="text-muted-foreground">English (US)</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Audience Profile Tab */}
                <TabsContent value="audience" className="mt-6">
                  <div className="bg-gradient-to-br from-green-50/30 via-white to-emerald-50/30 rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 max-h-[600px] overflow-y-auto">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-green-700">Interests & Preferences:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Coffee as a lifestyle marker and daily ritual</li>
                          <li>Exploring new café-inspired beverages and recipes</li>
                          <li>Following coffee trends, influencers, and baristas</li>
                          <li>Health-conscious, sustainability-minded consumers</li>
                          <li>Enjoy DIY, creative culinary experiences, and mindful living</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-green-700">Purchasing Habits:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Prefers convenience (supermarkets, e-commerce, quick delivery)</li>
                          <li>Seeks instant gratification with consistent quality</li>
                          <li>Values affordable luxury; willing to pay for ethically sourced products</li>
                          <li>Responds to seasonal flavors, limited editions, and sustainability claims</li>
                          <li>Mix of bulk family purchases and small daily buys</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-green-700">Pain Points:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Limited time for cafés or complex brewing</li>
                          <li>Desire for healthier, sustainable coffee options</li>
                          <li>Overwhelming product choices in the market</li>
                          <li>Need for value-for-money, without sacrificing flavor or ethics</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-green-700">Influence Drivers:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Digital media, influencer marketing, and peer recommendations</li>
                          <li>Barista trends, lifestyle endorsements, and seasonal promotions</li>
                          <li>Trust in heritage and brand transparency</li>
                          <li>Educational, recipe-based content that enhances product usage</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Customer Insights Tab */}
                <TabsContent value="insights" className="mt-6">
                  <div className="bg-gradient-to-br from-indigo-50/30 via-white to-violet-50/30 rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 max-h-[600px] overflow-y-auto">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-indigo-700">Key Feedback:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          Customers praise NESCAFÉ for consistent quality, affordability, and versatility—especially for creating café-style drinks at home. They appreciate smooth taste, instant convenience, and sustainable sourcing. Occasional feedback mentions flavor preferences or perceptions of artificial taste in certain blends.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-indigo-700">Usage Patterns:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          Used daily as part of morning routines and quick breaks. Popular for home coffee recipes like iced lattes, mochas, and shakes. Espresso Concentrate allows café-style drinks without machines. Also used for cold brews and seasonal beverage creations.
                        </p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-indigo-700">Product Strengths:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Ease of use and instant preparation</li>
                          <li>Wide variety of flavors and blends</li>
                          <li>Smooth taste and customizable strength</li>
                          <li>Affordable premium quality</li>
                          <li>Sustainable packaging and sourcing</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-indigo-700">Loyalty & Retention Patterns:</h3>
                        <p className="text-muted-foreground leading-relaxed">
                          Strong brand loyalty among habitual coffee drinkers. Repeat purchases driven by reliability, taste, and product innovation. Engagement is boosted by sustainability and creative recipe ideas. Even after lapses, customers often return for the familiarity and trust in NESCAFÉ's quality.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Competitive Landscape Tab */}
                <TabsContent value="competitive" className="mt-6">
                  <div className="bg-gradient-to-br from-teal-50/30 via-white to-blue-50/30 rounded-lg border border-gray-200 shadow-sm p-6 md:p-8 max-h-[600px] overflow-y-auto">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-teal-700">Industry:</h3>
                        <p className="text-muted-foreground">Coffee, Beverage, Instant Coffee Products</p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-teal-700">Main Competitors:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li><strong>Starbucks (Via Instant Coffee):</strong> Premium positioning with café-culture branding</li>
                          <li><strong>Jacobs Douwe Egberts (JDE):</strong> Strong European market presence through brands like L'Or and Senseo</li>
                          <li><strong>Folgers:</strong> North American leader focusing on affordability and accessibility</li>
                          <li><strong>Maxwell House:</strong> Established value brand known for reliability</li>
                          <li><strong>Café Bustelo:</strong> Niche Latin-inspired blends with cultural authenticity</li>
                          <li><strong>Tata Coffee:</strong> Indian market leader with global expansion</li>
                          <li><strong>Super & Owl (Super Coffeemix Mfg Pte Ltd):</strong> Asian competitors offering value mixes</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-teal-700">Competitor Strengths & Weaknesses:</h3>
                        <p className="text-muted-foreground mb-2"><strong>Strengths:</strong> Established trust, market coverage, tailored blends, and brand equity.</p>
                        <p className="text-muted-foreground"><strong>Weaknesses:</strong> Slower innovation, limited sustainability integration, and less global alignment compared to NESCAFÉ.</p>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-teal-700">Market Trends:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Rising demand for convenient yet premium instant coffee</li>
                          <li>Sustainability and ethical sourcing as core purchase drivers</li>
                          <li>Plant-based, sugar-free, and functional coffee variants gaining traction</li>
                          <li>Growth of direct-to-consumer and digital commerce models</li>
                          <li>Heightened consumer focus on transparency and eco-friendly packaging</li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-teal-700">Technological Innovations:</h3>
                        <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                          <li>Instant espresso and iced blends with café-quality crema</li>
                          <li>Cold-soluble coffee formulations for on-the-go convenience</li>
                          <li>Sustainable farming tech and recyclable packaging</li>
                          <li>AI-driven personalization and targeted marketing</li>
                          <li>Digital-led recipe sharing and community engagement</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>

            {/* CTA Button */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="text-center mt-12">
              <button onClick={() => setIsDialogOpen(true)} className="px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
                Get My Free Brand Report
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How gimmefy Knows Your Brand Section */}
      <section className="relative z-20 bg-gray-50/50 border-t border-gray-200 py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <BouncyCardsFeatures />
          
          {/* CTA Button */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="text-center mt-12">
            <button onClick={() => setIsDialogOpen(true)} className="px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-primary-foreground font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
              Take the first step
            </button>
          </motion.div>
        </div>
      </section>

      {/* Trusted by Marketing Leaders section */}
      <section className="relative z-20">
        <BrandReportClientsSection className="py-0" onCtaClick={() => setIsDialogOpen(true)} />
      </section>

      <FooterColumn />
      </div>

      <BrandAnalyzerDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>;
};
export default GetFreeBrandReportPage;